import { z } from 'zod';

// `trailingZeroDisplay` is typed in lib.es2023.intl while the shared tsconfig
// lib is ES2022; the runtime (Node ≥ 18, evergreen browsers) supports it.
type CurrencyIntlOptions = Intl.NumberFormatOptions & {
  trailingZeroDisplay?: 'auto' | 'stripIfInteger';
};

/**
 * Formats a number as a currency string using the French locale and Euro currency.
 *
 * @param value - The numeric value to format. Defaults to 0 if null or undefined.
 * @param options - Formatting options.
 * @param options.digits - Number of decimal places, or `'auto'` to show two
 *   decimals only when the cents are non-zero. Defaults to 0.
 * @param options.hasSymbol - Whether to include the currency symbol. Defaults to true.
 * @param options.isCompact - Whether to use compact notation (e.g., 1K, 1M). Defaults to false.
 * @returns The formatted currency string.
 *
 * @example
 * formatCurrency(1234.56) // "1 235 €"
 * formatCurrency(1234.56, { digits: 2 }) // "1 234,56 €"
 * formatCurrency(1234.56, { digits: 'auto' }) // "1 234,56 €"
 * formatCurrency(2000, { digits: 'auto' }) // "2 000 €"
 * formatCurrency(1234567, { isCompact: true }) // "1 M €"
 * formatCurrency(1234.56, { hasSymbol: false }) // "1 235"
 */
export const formatCurrency = (
  value?: number | null,
  options?: {
    digits?: number | 'auto';
    hasSymbol?: boolean;
    isCompact?: boolean;
  }
) => {
  let intlOptions: CurrencyIntlOptions = {};
  const defaultOptions = {
    hasSymbol: true,
    isCompact: false,
    digits: 0,
    ...options,
  };

  if (defaultOptions?.hasSymbol) {
    intlOptions =
      defaultOptions.digits === 'auto'
        ? {
            currency: 'EUR',
            style: 'currency',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            trailingZeroDisplay: 'stripIfInteger',
          }
        : {
            currency: 'EUR',
            style: 'currency',
            minimumFractionDigits: defaultOptions.digits,
            maximumFractionDigits: defaultOptions.digits,
          };
  }

  return new Intl.NumberFormat('fr-FR', {
    ...intlOptions,
    notation: defaultOptions.isCompact ? 'compact' : 'standard',
  }).format(value || 0);
};

/**
 * Converts a duration in months to years.
 *
 * @param value - The number of months.
 * @returns The equivalent number of complete years (floored).
 *
 * @example
 * formatMonthsToYear(24) // 2
 * formatMonthsToYear(30) // 2
 * formatMonthsToYear(36) // 3
 */
export const formatMonthsToYear = (value: number) => Math.floor(value) / 12;

/**
 * Formats a decimal number as a percentage string using the French locale.
 *
 * @param value - The decimal value to format (e.g., 0.125 for 12.5%).
 * @param options - Formatting options.
 * @param options.digits - Number of decimal places. Defaults to 2.
 * @param options.showPlusSign - Whether to display a "+" sign for positive values. Defaults to false.
 * @returns The formatted percentage string.
 *
 * @example
 * formatPercent(0.1234) // "12,34 %"
 * formatPercent(0.128, { digits: 1 }) // "12,8 %"
 * formatPercent(0.172, { showPlusSign: true }) // "+17,20 %"
 * formatPercent(-0.05, { showPlusSign: true }) // "-5,00 %"
 */
export const formatPercent = (
  value: number,
  options: { digits?: number; showPlusSign?: boolean } = {}
): string => {
  const { digits = 2, showPlusSign = false } = options;

  const formatter = new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay: showPlusSign ? 'exceptZero' : 'auto',
  });

  return formatter.format(value);
};

/**
 * Formats a number as a decimal string using the French locale.
 *
 * @param value - The numeric value to format.
 * @param options - Formatting options.
 * @param options.digits - Number of decimal places. Defaults to 2.
 * @param options.showPlusSign - Whether to display a "+" sign for positive values. Defaults to false.
 * @returns The formatted decimal string.
 *
 * @example
 * formatDecimal(1234.567) // "1 234,57"
 * formatDecimal(1234.5, { digits: 0 }) // "1 235"
 * formatDecimal(42, { showPlusSign: true }) // "+42,00"
 */
export const formatDecimal = (
  value: number,
  options: { digits?: number; showPlusSign?: boolean } = {}
): string => {
  const { digits = 2, showPlusSign = false } = options;

  const formatter = new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay: showPlusSign ? 'exceptZero' : 'auto',
  });

  return formatter.format(value);
};

/**
 * Generates a random numeric code of the specified length.
 *
 * @param length - The number of digits in the generated code.
 * @returns A random integer with exactly the specified number of digits.
 *
 * @example
 * generateDigitCode(4) // 1234 (random 4-digit number between 1000-9999)
 * generateDigitCode(6) // 123456 (random 6-digit number between 100000-999999)
 */
export const generateDigitCode = (length: number): number =>
  Math.floor(10 ** (length - 1) + Math.random() * 9 * 10 ** (length - 1));

/**
 * Formats an IBAN string by inserting spaces every 4 characters.
 *
 * @param iban - The raw IBAN string without spaces.
 * @returns The formatted IBAN with spaces between each group of 4 characters.
 *
 * @example
 * formatIBAN("FR7630001007941234567890185") // "FR76 3000 1007 9412 3456 7890 185"
 */
export const formatIBAN = (iban: string) =>
  iban.replace(/(.{4})/g, '$1 ').trim();

/**
 * Rounds a number to a fixed precision to avoid floating-point arithmetic errors.
 *
 * @param value - The number to round.
 * @param precision - The number of decimal places to preserve. Defaults to 4.
 * @returns The rounded number.
 *
 * @example
 * roundToFixedPrecision(0.1 + 0.2) // 0.3 (instead of 0.30000000000000004)
 * roundToFixedPrecision(1.23456789, 2) // 1.23
 */
export const roundToFixedPrecision = (value: number, precision = 4): number =>
  Math.round(value * 10 ** precision) / 10 ** precision;

/**
 * Rounds a number down to the nearest multiple of `step` (the "arrondi inférieur"
 * used for capital tiers, e.g. capital must move by 1 000 € increments). When
 * `step` is not strictly positive the value is returned unchanged.
 *
 * @param value - The number to round down.
 * @param step - The multiple to align on (e.g. 1000).
 * @returns The largest multiple of `step` that is <= `value`.
 *
 * @example
 * floorToMultiple(84_959, 1_000) // 84_000  (Arrondi(84 959 ; -3))
 * floorToMultiple(152_499, 1_000) // 152_000
 */
export const floorToMultiple = (value: number, step: number): number =>
  step > 0 ? Math.floor(value / step) * step : value;

/**
 * Checks that a string is a plain non-negative decimal number made of digits only,
 * with at most `maxDecimals` digits after the dot. Rejects scientific notation
 * ("1.5e-7"), signs, comma decimals and any other non-digit character.
 *
 * @param value - The string to check.
 * @param maxDecimals - The maximum number of digits allowed after the dot. Defaults to 4.
 * @returns True when the string is a plain decimal within the precision limit.
 *
 * @example
 * isPlainDecimalString("0.1234") // true
 * isPlainDecimalString("12") // true
 * isPlainDecimalString("0.12345") // false (5 decimals)
 * isPlainDecimalString("1.5e-7") // false (scientific notation)
 */
export const isPlainDecimalString = (value: string, maxDecimals = 4): boolean =>
  new RegExp(`^\\d+(\\.\\d{1,${maxDecimals}})?$`).test(value);

type AssertNumber = (value: unknown, error?: string) => asserts value is number;

/**
 * Asserts that a value is a number, throwing an error if validation fails.
 * Uses Zod for runtime validation with TypeScript type narrowing.
 *
 * @param value - The value to validate.
 * @param error - Custom error message if validation fails. Defaults to "Number required".
 * @throws Error if the value is not a valid number.
 *
 * @example
 * const input: unknown = 42;
 * assertNumber(input);
 * // input is now typed as number
 *
 * assertNumber("not a number", "Invalid input"); // throws Error: "Invalid input"
 */
export const assertNumber: AssertNumber = (value, error) => {
  const result = z.number().safeParse(value);
  if (!result.success) {
    throw new Error(error ?? 'Number required');
  }
};
